import React from 'react';
import { Popup } from 'semantic-ui-react';
import { CustomLabel } from '../common';
import PropTypes from 'prop-types';
import './OpportunityStatusBadge.css';
import readerIconA from '../../assets/reader-icon-c.svg';
import arrowUp from '../../assets/arrow-up.svg';
import arrowDown from '../../assets/arrow-down.svg';
import arrowLeft from '../../assets/arrow-left.svg';
import arrowRight from '../../assets/arrow-right.svg';
import completedCheck from '../../assets/check-circle.svg';
import openIcon from '../../assets/open-icon-puzzle.svg';
import cancelledIcon from '../../assets/cancelled-rect.svg';
/**
 * OpportunityStatusBadge component displays a status badge for opportunities
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - The status to display
 * @param {string} props.priority - The priority to display
 * @param {string} props.deadline - The deadline to display
 * @param {boolean} props.showIcon - Whether to show an icon
 * @param {boolean} props.showTooltip - Whether to show a tooltip
 * @param {string} props.size - The size of the badge ('mini', 'tiny', 'small', 'medium', 'large')
 */
const OpportunityStatusBadge = ({
  status,
  priority,
  deadline,
  showIcon = true,
  showTooltip = true,
  size = 'tiny'
}) => {
  // Get the status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'green';
      case 'Assigned':
        return 'blue';
      case 'Completed':
        return 'purple';
      case 'Cancelled':
        return 'red';
      default:
        return 'grey';
    }
  };

  // Get the priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'red';
      case 'Medium':
        return 'yellow';
      case 'Low':
        return 'blue';
      default:
        return 'grey';
    }
  };

  // Get the status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
        return openIcon;
      case 'Assigned':
        return readerIconA;
      case 'Completed':
        return completedCheck; //'check';
      case 'Cancelled':
        return cancelledIcon;
      default:
        return 'question';
    }
  };

  // Get the priority icon - returns actual SVG import
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High':
        return arrowUp;
      case 'Medium':
        return arrowRight;  // Using arrow-left for medium priority
      case 'Low':
        return arrowDown;
      default:
        return readerIconA;  // Default icon
    }
  };

  // Render the status badge (using CustomLabel with SVG icon)
  const statusBadge = status && (
    <CustomLabel
      color={getStatusColor(status)}
      size={size}
      icon={showIcon ? getStatusIcon(status) : null}
      style={{ width: "100px" }}
    >
      {status}
    </CustomLabel>
  );


  // Render the priority badge if priority is provided
  const priorityBadge = priority && (
    <CustomLabel
      color={getPriorityColor(priority)}
      size={size}
      icon={showIcon ? getPriorityIcon(priority) : null}
    >
      {priority} Priority
    </CustomLabel>
  );

  // Render the deadline badge if deadline is provided
  const deadlineBadge = deadline && (
    <CustomLabel
      color="grey"
      size={size}
      icon={showIcon ? readerIconA : null}
    >
      Due: {new Date(deadline).toLocaleDateString()}
    </CustomLabel>
  );

  // If tooltips are disabled, just render the badges
  if (!showTooltip) {
    return (
      <div className="opportunity-status-badges">
        {statusBadge}
        {priorityBadge}
        {deadlineBadge}
      </div>
    );
  }

  // Render the badges with tooltips
  return (
    <div className="opportunity-status-badges">
      <Popup
        content={`Status: ${status}`}
        trigger={statusBadge}
        position="top center"
      />

      {priority && (
        <Popup
          content={`Priority: ${priority}`}
          trigger={priorityBadge}
          position="top center"
        />
      )}

      {deadline && (
        <Popup
          content={`Deadline: ${new Date(deadline).toLocaleDateString()}`}
          trigger={deadlineBadge}
          position="top center"
        />
      )}
    </div>
  );
};

OpportunityStatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  priority: PropTypes.string,
  deadline: PropTypes.string,
  showIcon: PropTypes.bool,
  showTooltip: PropTypes.bool,
  size: PropTypes.oneOf(['mini', 'tiny', 'small', 'medium', 'large'])
};

export default OpportunityStatusBadge;
